import { KeyboardEvent, MouseEvent, useCallback, useEffect, useState } from 'react'

import { NumberInput } from 'cng-common/components/numberInput'

import { renderGrowthValueRange } from '../../helpers/renderGrowthValue'
import { isGrowthValueRange, TGrowthValue } from '../../types'

import styles from './GrowthValueInput.module.scss'

// null and mixed (range) cells start from an empty input; committing a
// number overwrites every record the cell aggregates.
const toDraft = (value: TGrowthValue): string => (typeof value === 'number' ? String(value) : '')

interface IGrowthValueInputProps {
  value: TGrowthValue
  onCommit: (value: number) => void
}

export const GrowthValueInput = ({ value, onCommit }: IGrowthValueInputProps) => {
  const [draft, setDraft] = useState(() => toDraft(value))
  const [isEditing, setIsEditing] = useState(() => !isGrowthValueRange(value))
  const [autoFocus, setAutoFocus] = useState(false)

  useEffect(() => {
    setDraft(toDraft(value))
    if (!isGrowthValueRange(value)) {
      setIsEditing(true)
    }
  }, [value])

  const onBlur = useCallback(() => {
    const parsed = Number(draft.trim())
    const rounded = Number(parsed.toFixed(1))

    if (draft.trim() && !Number.isNaN(parsed) && rounded !== value) {
      onCommit(rounded)
      return
    }

    setDraft(toDraft(value))
    if (isGrowthValueRange(value)) {
      setIsEditing(false)
    }
  }, [draft, value, onCommit])

  // IInputCommonProps exposes no onKeyDown, so Enter is routed through the
  // blur commit path via a wrapper handler.
  const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      ;(event.target as HTMLInputElement).blur()
    }
  }, [])

  const onMixedClick = useCallback((event: MouseEvent) => {
    event.stopPropagation()
    setAutoFocus(true)
    setIsEditing(true)
  }, [])

  if (!isEditing && isGrowthValueRange(value)) {
    return renderGrowthValueRange({ value, onClick: onMixedClick })
  }

  return (
    // Clicks must not bubble to the row: on group rows they would toggle
    // expand/collapse while the user is focusing the input.
    <div className={styles.root} onKeyDown={onKeyDown} onClick={(event) => event.stopPropagation()}>
      <NumberInput
        value={draft}
        onChange={setDraft}
        onBlur={onBlur}
        autoFocus={autoFocus}
        align="center"
        hasSpin={false}
        useGrouping={false}
        maximumFractionDigits={1}
      />
    </div>
  )
}

GrowthValueInput.displayName = 'GrowthValueInput'
